import classnames from 'classnames'
import { twMerge } from 'tailwind-merge'

/**
 * Fonction utilitaire pour combiner et merger des classes CSS conditionnelles.
 *
 * Cette fonction combine la puissance de `classnames` pour la logique conditionnelle
 * avec `tailwind-merge` pour résoudre intelligemment les conflits de classes
 * Tailwind CSS. Elle permet d'écrire des combinaisons de classes lisibles avec
 * conditions tout en évitant les doublons et conflits.
 *
 * Fonctionnalités :
 * - Support des chaînes, objets et tableaux de classes
 * - Logique conditionnelle avec classnames
 * - Résolution automatique des conflits Tailwind avec twMerge
 * - Déduplication intelligente des classes CSS
 * - Performance optimisée pour les composants React
 *
 * @param inputs - Classes CSS sous différents formats (string, object, array)
 * @returns Chaîne de classes CSS mergées et optimisées
 *
 * @example
 * ```tsx
 * // Classes conditionnelles simples
 * <div className={cn(
 *   'btn btn-primary',
 *   isLoading && 'loading',
 *   disabled && 'btn-disabled'
 * )} />
 *
 * // Résolution de conflits Tailwind
 * <div className={cn(
 *   'px-4 py-2', // padding par défaut
 *   large && 'px-8 py-4' // remplace le padding si large=true
 * )} />
 *
 * // Objets conditionnels
 * <button className={cn('btn', {
 *   'btn-primary': variant === 'primary',
 *   'btn-secondary': variant === 'secondary',
 *   'btn-lg': size === 'large'
 * })} />
 *
 * // Avec classes custom et props
 * <div className={cn(
 *   'default-styles',
 *   customClassName,
 *   { 'active-state': isActive }
 * )} />
 * ```
 */
export function cn(...inputs: classnames.ArgumentArray) {
  return twMerge(classnames(inputs))
}
